/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaPowderProductMasterService } from '../../../../../service/mediaPowderProductMaster';
import MediaProductDefinitionForm from '../../../../common/commonComponents/mediaProductDefinitionForm';


interface AddPowderProductProps {
  onSave: () => void;
  onCancel: () => void;
  showToastMessage: (message: string, type: string) => void;
}

const AddPowderProduct: React.FC<AddPowderProductProps> = ({ onSave, onCancel, showToastMessage }) => {
    const { t } = useTranslation();
    const [mediaList, setMediaList] = useState<any[]>([]);
    const [supplierList, setSupplierList] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
      selectedMedia: "",
      selectedMediaCode: "",
      supplierId: "",
      supplierCode: "", 
      catalogNumber: "",
      initialPh: "",
      initialPhTolerance: "0.2",
      finalPh: "",
      finalPhTolerance: "0.2",
      referenceTemp: "",
      shelfLife: "",
    });

  interface Errors {
    selectedMedia?: string;
    supplierId?: string;
    catalogNumber?: string;
    shelfLife?: string;
    initialPh?: string;
    finalPh?: string;
    referenceTemp?: string;
  }

  const [errors, setErrors] = useState<Errors>({});
  const initialPh = parseFloat(formData.initialPh);
  const finalPh = parseFloat(formData.finalPh);
  const referenceTemp = parseFloat(formData.referenceTemp);
  const shelfLife = parseInt(formData.shelfLife, 10);


  const validateForm = () => {
      const newErrors: Errors = {};
      if (!formData.selectedMedia) {newErrors.selectedMedia = t("mediaPowderProductMaster.errors.mediaName");}
      if (!formData.supplierId) {newErrors.supplierId = t("mediaPowderProductMaster.errors.supplier");}
      if (!formData.catalogNumber) {newErrors.catalogNumber = t("mediaPowderProductMaster.errors.catalogNumber");}
      if (!formData.shelfLife) {newErrors.shelfLife = t("mediaPowderProductMaster.errors.shelfLife");}
      if (!formData.initialPh) {newErrors.initialPh = t("mediaPowderProductMaster.errors.initialPh");}
      if (!formData.finalPh) {newErrors.finalPh = t("mediaPowderProductMaster.errors.finalPh");}
      if (!formData.referenceTemp) {newErrors.referenceTemp = t("mediaPowderProductMaster.errors.referenceTemp");}
      if (
          formData.shelfLife &&
          (isNaN(shelfLife) || shelfLife <= 0)
        ) {
          newErrors.shelfLife = "Shelf life must be greater than 0";
        }
      if (formData.initialPh &&
        (isNaN(initialPh) || initialPh < 0 || initialPh > 14)
      ) {
        newErrors.initialPh = "Initial pH must be between 0 and 14";
      }
      if (
        formData.finalPh &&
        (isNaN(finalPh) || finalPh < 0 || finalPh > 14)
      ) {
        newErrors.finalPh = "Final pH must be between 0 and 14";
      }
      if (
        formData.referenceTemp &&
        (isNaN(referenceTemp) || referenceTemp < 2 || referenceTemp > 80)
      ) {
        newErrors.referenceTemp = "Temperature must be between 2°C and 80°C";
      }

      if (Object.keys(newErrors).length > 0) {setErrors(newErrors);return false;}
      setErrors({});
      return true;
    };

    const handleChange = (field: string, value: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleMediaChange = (value: string) => {
      const selected = mediaList.find((item: any) => item.id.toString() === value);
      setFormData(prev => ({
        ...prev,
        selectedMedia: value,
        selectedMediaCode: selected?.media_code || "",
      }));
    };


    //get media master list for dropdown
    const getMediaList = async () => {
    try {
        const response = await MediaPowderProductMasterService.getMediaMasterList();

        if (response?.status && response.statusCode === 200) {
        setMediaList(response.data);
        }
    } catch (error) {
        console.error("Media list error:", error);
    }
    };



    //get suppliers list
    const getSuppliers = async () => {
      try {
        const response = await MediaPowderProductMasterService.getSuppliersByMedia();

        if (response?.status && response.statusCode === 200) {
          setSupplierList(response.data);
        }
      } catch (error) {
        console.error("Supplier list error:", error);
      }
    };
    
    

  //create media product definition  
  const handleSave = async () => {
     if (isSaving) return;
    if (!validateForm()) return;
    const payload = {
      media_master_id: formData.selectedMedia,
      supplier_id: formData.supplierId,
      media_code: formData.selectedMediaCode,  
      supplier_code: formData.supplierCode, 
      catalog_number: formData.catalogNumber,
      initial_ph: formData.initialPh,
      initial_ph_tolerance: formData.initialPhTolerance,
      final_ph: formData.finalPh,
      final_ph_tolerance: formData.finalPhTolerance,
      reference_temperature: `${formData.referenceTemp}°C`,
      shelf_life_days: formData.shelfLife,
      sterility_requirement_id: "1",
      interface_name: "Media Powder Product Master",
      // media_code: "TSA",
      // supplier_code: "HIMEDIA",
    };

    setIsSaving(true);
    try {
      setIsSaving(true);

      const response =
        await MediaPowderProductMasterService.createMediaProductDefinition(payload);

      if (response?.status) {
        showToastMessage(response?.message, "success");
        onSave();
        return;
      } else {
        showToastMessage(response?.message, "error");
      }
    } catch (error) {
      showToastMessage(t("mediaPowder.errors.errorSaving"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    getMediaList();
    getSuppliers();
  }, []);  
  

  return (
    <MediaProductDefinitionForm
      mediaList={mediaList}
      supplierList={supplierList}
      formData={formData}
      onChange={handleChange}
      onMediaChange={handleMediaChange}
      onSave={handleSave}
      isSaving={isSaving}
      onCancel={onCancel}
      errors={errors}
    />
  );
};

export default AddPowderProduct;
